import loadable from "@loadable/component";
import pMinDelay from "p-min-delay";
import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Button, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import Select from "react-select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Sector, Tooltip } from "recharts";
import api from "../../../api";
import { ThemeContext } from "../../../context/ThemeContext";
import usePermission from "../../../hooks/usePermission";
import { getQCStats } from "../../../services/qcService";
import { getChecklistStats } from "../../../services/safetyChecklistService";
import { getSafetyStats } from "../../../services/safetyService";
import PermissionWrapper from "../PermissionWrapper";

/* ================= Lazy Components ================= */
const ChartBarApex = loadable(() =>
    pMinDelay(import("./Dashboard/ChartBarApex"), 300),
);
const ReservationChart = loadable(() =>
    pMinDelay(import("./Dashboard/ReservationChart"), 300),
);
const IndiaMap = loadable(() => pMinDelay(import("./Dashboard/IndiaMap"), 300));

const COLORS = [
    "#0078D7", // CX360 Brand Blue
    "#1BA8F5",
    "#F47920", // CX360 Brand Orange
    "#28A745",
    "#FFC107",
    "#17A2B8",
    "#DC3545",
    "#6F42C1",
    "#20C997",
    "#546E7A",
    "#E91E63",
    "#FF9800"
];

const BarTooltip = ({ active, payload, isCurrency, total, isDark }) => {
    if (!active || !payload?.length) return null;

    const item = payload[0];
    const name = item?.payload?.name || "Unnamed";
    const value = Number(item?.value || 0);
    const share = total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";

    return (
        <div
            style={{
                background: isDark ? "rgba(30, 41, 59, 0.96)" : "rgba(255, 255, 255, 0.98)",
                border: isDark ? "1px solid rgba(71, 85, 105, 0.6)" : "1px solid rgba(226, 232, 240, 0.9)",
                borderRadius: 12,
                boxShadow: isDark ? "0 10px 25px -5px rgba(0, 0, 0, 0.5)" : "0 10px 25px -5px rgba(15, 23, 42, 0.15)",
                padding: "10px 14px",
                minWidth: 200,
                backdropFilter: "blur(6px)",
            }}
        >
            <div
                className="fw-bold mb-1 small"
                style={{
                    maxWidth: 260,
                    wordBreak: 'break-word',
                    color: isDark ? "#ffffff" : "#0f172a"
                }}
            >
                {name}
            </div>
            <div className="d-flex justify-content-between gap-3 small" style={{ color: isDark ? "#94a3b8" : "#64748b" }}>
                <span>Amount:</span>
                <span className="fw-bold" style={{ color: isDark ? "#38bdf8" : "#0078D7" }}>
                    {isCurrency ? `₹${value.toLocaleString('en-IN')}` : value.toLocaleString('en-IN')}
                </span>
            </div>
            <div className="d-flex justify-content-between gap-3 small" style={{ color: isDark ? "#94a3b8" : "#64748b" }}>
                <span>Share:</span>
                <span className="fw-bold" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>{share}%</span>
            </div>
        </div>
    );
};

const PieTooltip = ({ active, payload, isCurrency, total, isDark }) => {
    if (!active || !payload?.length) return null;

    const item = payload[0];
    const name = item?.payload?.name || "Unnamed";
    const value = Number(item?.value || 0);
    const share = total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";

    return (
        <div
            style={{
                background: isDark ? "rgba(30, 41, 59, 0.96)" : "#ffffff",
                border: isDark ? "1px solid rgba(71, 85, 105, 0.6)" : "1px solid rgba(226, 232, 240, 0.9)",
                borderRadius: 12,
                boxShadow: isDark ? "0 10px 30px rgba(0, 0, 0, 0.5)" : "0 10px 30px rgba(15, 23, 42, 0.16)",
                padding: "10px 12px",
                minWidth: 180,
                backdropFilter: "blur(6px)",
            }}
        >
            <div className="fw-bold mb-1" style={{ color: isDark ? "#ffffff" : "#0f172a", fontSize: 13 }}>
                {name}
            </div>
            <div className="d-flex justify-content-between gap-3 small" style={{ color: isDark ? "#94a3b8" : "#475569" }}>
                <span>Value</span>
                <span className="fw-semibold" style={{ color: isDark ? "#38bdf8" : "#0078D7" }}>
                    {isCurrency ? `₹${value.toLocaleString('en-IN')}` : value.toLocaleString('en-IN')}
                </span>
            </div>
            <div className="d-flex justify-content-between gap-3 small" style={{ color: isDark ? "#94a3b8" : "#475569" }}>
                <span>Share</span>
                <span className="fw-semibold" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>{share}%</span>
            </div>
        </div>
    );
};

const renderActivePieShape = (props, isDark = false) => {
    const RADIAN = Math.PI / 180;
    const {
        cx,
        cy,
        midAngle,
        innerRadius,
        outerRadius,
        startAngle,
        endAngle,
        fill,
        payload,
        value,
        percent,
    } = props;

    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const highlightRadius = outerRadius + 2;
    const labelRadius = outerRadius + 14;

    const sx = cx + highlightRadius * cos;
    const sy = cy + highlightRadius * sin;
    const mx = cx + labelRadius * cos;
    const my = cy + labelRadius * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 18;
    const ey = my;

    return (
        <g>
            <text x={cx} y={cy - 6} textAnchor="middle" fill={isDark ? "#ffffff" : "#0f172a"} fontSize={11} fontWeight={700}>
                {payload?.name}
            </text>
            <text x={cx} y={cy + 8} textAnchor="middle" fill={fill} fontSize={11} fontWeight={600}>
                {typeof value === "number" ? value.toLocaleString("en-IN") : value}
            </text>
            <text x={cx} y={cy + 22} textAnchor="middle" fill={isDark ? "#94a3b8" : "#64748b"} fontSize={9}>
                {Number.isFinite(percent) ? `${(percent * 100).toFixed(1)}%` : ""}
            </text>
            <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
            <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
            <Sector
                cx={cx}
                cy={cy}
                innerRadius={innerRadius}
                outerRadius={highlightRadius}
                startAngle={startAngle}
                endAngle={endAngle}
                fill={fill}
            />
            <Sector
                cx={cx}
                cy={cy}
                innerRadius={outerRadius}
                outerRadius={outerRadius + 3}
                startAngle={startAngle}
                endAngle={endAngle}
                fill={fill}
                opacity={0.16}
            />
        </g>
    );
};

const formatIndianCurrency = (val) => {
    const num = Number(val || 0);
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
    if (num >= 1000) return `₹${(num / 1000).toFixed(1)} K`;
    return `₹${num.toLocaleString('en-IN')}`;
};

const StatusPie = ({ title, data, isCurrency = true, customTotal, hideCardWrapper = false, isWide = false }) => {
    const { background } = useContext(ThemeContext);
    const isDark = background?.value === "dark" || (typeof document !== "undefined" && document.body.getAttribute("data-theme-version") === "dark");

    const chartWrapRef = useRef(null);
    const hoverTimerRef = useRef(null);
    const [isInView, setIsInView] = useState(false);
    const [hasAnimated, setHasAnimated] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);

    // Filter non-zero items and sort descending
    const formattedAll = useMemo(() => {
        return (data || [])
            .filter(item => Number(item?.value || 0) > 0)
            .map(item => ({
                name: item.name || "Unnamed",
                value: Number(item.value || 0),
            }))
            .sort((a, b) => b.value - a.value);
    }, [data]);

    const total = useMemo(() => {
        if (customTotal !== undefined) return customTotal;
        return formattedAll.reduce((sum, item) => sum + item.value, 0);
    }, [formattedAll, customTotal]);

    // Condition: Up to 10 items -> Pie Chart. More than 10 items -> Automatic Full-Width Bar Graph
    const isBar = isWide || formattedAll.length > 10;

    const hasRenderableData = formattedAll.length > 0 && total > 0;

    useEffect(() => {
        const node = chartWrapRef.current;
        if (!node) return;

        if (typeof IntersectionObserver === "undefined") {
            setIsInView(true);
            setHasAnimated(true);
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry?.isIntersecting) {
                    setIsInView(true);
                    setHasAnimated(true);
                    observer.disconnect();
                }
            },
            {
                threshold: 0.25,
                rootMargin: "0px 0px -10% 0px",
            }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    useEffect(() => () => {
        if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current);
        }
    }, []);

    const scheduleActiveIndex = useCallback((index) => {
        if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current);
        }

        hoverTimerRef.current = setTimeout(() => {
            setActiveIndex(index);
        }, 80);
    }, []);

    const innerContent = (
        <div
            ref={chartWrapRef}
            className="card-body p-0 d-flex flex-column justify-content-center"
            style={{ minHeight: isBar ? 400 : 420 }}
        >
            {!hasRenderableData ? (
                <div className="d-flex flex-column align-items-center justify-content-center h-100 p-4">
                    <div className="rounded-circle bg-light d-flex align-items-center justify-content-center mb-2" style={{ width: 48, height: 48 }}>
                        <i className="fas fa-chart-pie text-muted fs-4"></i>
                    </div>
                    <p className="text-muted small mb-0 fw-semibold">No data available</p>
                    <span className="text-muted" style={{ fontSize: 11 }}>No active records for this metric</span>
                </div>
            ) : isBar ? (
                /* =========================================
                   BAR GRAPH (Automatic for >10 items in Complete Width)
                   ========================================= */
                <div className="p-3 d-flex flex-column w-100" style={{ height: 400 }}>
                    <div style={{ width: "100%", height: "100%" }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={formattedAll}
                                margin={{ top: 20, right: 30, left: 10, bottom: 80 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"} />
                                <XAxis
                                    dataKey="name"
                                    interval={0}
                                    tick={({ x, y, payload }) => {
                                        const text = payload.value || "";
                                        const maxLen = 22;
                                        const truncated = text.length > maxLen ? `${text.substring(0, maxLen - 2)}...` : text;
                                        return (
                                            <g transform={`translate(${x},${y})`}>
                                                <text
                                                    x={0}
                                                    y={0}
                                                    dy={14}
                                                    textAnchor="end"
                                                    fill={isDark ? "#94a3b8" : "#64748b"}
                                                    fontSize={11.5}
                                                    fontWeight={500}
                                                    transform="rotate(-35)"
                                                >
                                                    {truncated}
                                                </text>
                                            </g>
                                        );
                                    }}
                                />
                                <YAxis
                                    tickFormatter={(val) => isCurrency ? formatIndianCurrency(val) : val}
                                    tick={{ fill: isDark ? "#94a3b8" : "#64748b", fontSize: 11 }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <Tooltip
                                    content={<BarTooltip isCurrency={isCurrency} total={total} isDark={isDark} />}
                                    cursor={{ fill: isDark ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 120, 215, 0.06)" }}
                                />
                                <Bar
                                    dataKey="value"
                                    radius={[6, 6, 0, 0]}
                                    animationDuration={1000}
                                >
                                    {formattedAll.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={COLORS[index % COLORS.length]}
                                        />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            ) : (
                /* =========================================
                   PIE / DONUT CHART (Automatic for <=10 items)
                   ========================================= */
                <div className="d-flex flex-column h-100 justify-content-between" style={{ minHeight: 420 }}>
                    <div style={{ width: '100%', height: 260, position: 'relative' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart margin={{ top: 10, right: 20, bottom: 10, left: 20 }}>
                                <Pie
                                    data={formattedAll}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={68}
                                    outerRadius={92}
                                    activeIndex={activeIndex}
                                    activeShape={(props) => renderActivePieShape(props, isDark)}
                                    stroke={isDark ? "#202020" : "#ffffff"}
                                    strokeWidth={2}
                                    paddingAngle={3}
                                    isAnimationActive={hasAnimated || isInView}
                                    animationBegin={100}
                                    animationDuration={1200}
                                    onMouseEnter={(_, index) => scheduleActiveIndex(index)}
                                    onMouseLeave={() => {
                                        if (hoverTimerRef.current) {
                                            clearTimeout(hoverTimerRef.current);
                                        }
                                        setActiveIndex(-1);
                                    }}
                                    label={false}
                                >
                                    {formattedAll.map((entry, index) => (
                                        <Cell
                                            key={index}
                                            fill={COLORS[index % COLORS.length]}
                                            style={{
                                                transition: "opacity 200ms ease, filter 200ms ease",
                                                filter: activeIndex === index ? "drop-shadow(0 6px 12px rgba(15, 23, 42, 0.15))" : "none",
                                            }}
                                            opacity={activeIndex === -1 || activeIndex === index ? 1 : 0.4}
                                        />
                                    ))}
                                </Pie>
                                <Tooltip
                                    content={<PieTooltip isCurrency={isCurrency} total={total} isDark={isDark} />}
                                    animationDuration={200}
                                />
                                {activeIndex === -1 && (
                                    <>
                                        <text x="50%" y="46%" textAnchor="middle" dominantBaseline="middle" fill={isDark ? "#94a3b8" : "#64748b"} fontSize={10} fontWeight={600} letterSpacing="0.5px">
                                            TOTAL
                                        </text>
                                        <text x="50%" y="55%" textAnchor="middle" dominantBaseline="middle" fill={isDark ? "#ffffff" : "#0f172a"} fontSize={14} fontWeight={700}>
                                            {isCurrency ? formatIndianCurrency(total) : total.toLocaleString('en-IN')}
                                        </text>
                                    </>
                                )}
                            </PieChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Compact Custom Legend */}
                    <div className="px-3 pb-3 mt-auto" style={{ maxHeight: 150, overflowY: 'auto' }}>
                        <div className="row g-1">
                            {formattedAll.map((item, idx) => {
                                const share = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
                                const color = COLORS[idx % COLORS.length];
                                const isHovered = activeIndex === idx;

                                return (
                                    <div key={idx} className="col-6 text-start">
                                        <div
                                            className="d-flex align-items-center gap-1 p-1 rounded transition-all"
                                            style={{
                                                fontSize: '11px',
                                                cursor: 'pointer',
                                                background: isHovered ? (isDark ? 'rgba(0,120,215,0.2)' : 'rgba(0,120,215,0.08)') : 'transparent',
                                                border: isHovered ? '1px solid rgba(0,120,215,0.3)' : '1px solid transparent'
                                            }}
                                            onMouseEnter={() => setActiveIndex(idx)}
                                            onMouseLeave={() => setActiveIndex(-1)}
                                        >
                                            <span
                                                style={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    backgroundColor: color,
                                                    flexShrink: 0
                                                }}
                                            />
                                            <span className="text-truncate flex-grow-1" title={item.name} style={{ color: isHovered ? '#0078D7' : (isDark ? '#cbd5e1' : '#334155') }}>
                                                {item.name}
                                            </span>
                                            <span className="fw-bold text-nowrap" style={{ fontSize: '10.5px', color: isDark ? '#ffffff' : '#0f172a' }}>
                                                {share}%
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    if (hideCardWrapper) {
        return innerContent;
    }

    return (
        <div className="card shadow-sm h-100 border-0 overflow-hidden">
            <div className="card-header border-0 d-flex flex-wrap justify-content-between align-items-center p-3 bg-transparent gap-2">
                <div>
                    <h6 className="fw-bold mb-0" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>{title}</h6>
                    {hasRenderableData && (
                        <small className="text-muted" style={{ fontSize: 11 }}>
                            {formattedAll.length} {formattedAll.length === 1 ? 'record' : 'records'}
                            {isBar && <span className="badge bg-primary-subtle text-primary ms-2">Bar Graph</span>}
                        </small>
                    )}
                </div>

                <span className="badge bg-primary rounded-pill fw-bold" style={{ fontSize: 11 }}>
                    Total: {isCurrency ? `₹${total.toLocaleString('en-IN')}` : total}
                </span>
            </div>

            {innerContent}
        </div>
    );
};

const FinancialGraph = ({ data, totals, hasPermission }) => {
    const { background } = useContext(ThemeContext);
    const isDark = background?.value === "dark" || (typeof document !== "undefined" && document.body.getAttribute("data-theme-version") === "dark");

    const [financialTab, setFinancialTab] = useState("ebitda"); // 'ebitda' | 'pat'

    const canViewEbitda = hasPermission("view:dashboard:ebitda");
    const canViewPat = hasPermission("view:dashboard:pat");
    const canViewFixed = hasPermission("view:dashboard:fixed-expenses");
    const canViewRecurring = hasPermission("view:dashboard:recurring-expenses");

    const ebitdaData = useMemo(() => (data || []).map(d => ({ name: d.name, value: Number(d.ebitda || 0) })), [data]);
    const patData = useMemo(() => (data || []).map(d => ({ name: d.name, value: Number(d.pat || 0) })), [data]);
    const fixedData = useMemo(() => (data || []).map(d => ({ name: d.name, value: Number(d.fixed || 0) })), [data]);
    const recurringData = useMemo(() => (data || []).map(d => ({ name: d.name, value: Number(d.recurring || 0) })), [data]);

    const totalFixed = useMemo(() => fixedData.reduce((s, i) => s + i.value, 0), [fixedData]);
    const totalRecurring = useMemo(() => recurringData.reduce((s, i) => s + i.value, 0), [recurringData]);
    const totalExpenses = totalFixed + totalRecurring;

    const activeProfitData = financialTab === "ebitda" ? ebitdaData : patData;
    const activeProfitTotal = financialTab === "ebitda" ? totals?.ebitda : totals?.pat;

    const hasProfitCards = canViewEbitda || canViewPat;
    const hasExpenseCards = canViewFixed || canViewRecurring;

    if (!canViewEbitda && !canViewPat && !canViewFixed && !canViewRecurring) {
        return null;
    }

    return (
        <div className="row g-3 mb-4">
            {/* CARD 1: Profitability (EBITDA & PAT Breakdown) */}
            {hasProfitCards && (
                <div className={hasExpenseCards ? "col-xl-7 col-lg-12 mb-3" : "col-12 mb-3"}>
                    <div className="card shadow-sm h-100 border-0 overflow-hidden">
                        <div className="card-header border-0 d-flex flex-wrap justify-content-between align-items-center p-3 bg-transparent gap-2">
                            <div className="d-flex align-items-center gap-2">
                                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 38, height: 38, backgroundColor: "rgba(40, 167, 69, 0.1)" }}>
                                    <i className="fas fa-hand-holding-usd text-success"></i>
                                </div>
                                <div>
                                    <h6 className="fw-bold mb-0" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>Operating Profitability Breakdown</h6>
                                    <small className="text-muted" style={{ fontSize: 11 }}>Project contribution to EBITDA & PAT</small>
                                </div>
                            </div>

                            <div className="d-flex align-items-center gap-2">
                                {(canViewEbitda && canViewPat) && (
                                    <div className="btn-group btn-group-sm rounded-pill p-1 border" style={{ background: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc', borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0' }}>
                                        <button
                                            type="button"
                                            className={`btn btn-sm py-1 px-3 rounded-pill border-0 ${financialTab === "ebitda" ? "btn-success text-white shadow-sm" : "text-muted"}`}
                                            style={{ fontSize: 11, fontWeight: 600 }}
                                            onClick={() => setFinancialTab("ebitda")}
                                        >
                                            EBITDA: {formatIndianCurrency(totals?.ebitda || 0)}
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn btn-sm py-1 px-3 rounded-pill border-0 ${financialTab === "pat" ? "btn-primary shadow-sm" : "text-muted"}`}
                                            style={{ fontSize: 11, fontWeight: 600 }}
                                            onClick={() => setFinancialTab("pat")}
                                        >
                                            PAT: {formatIndianCurrency(totals?.pat || 0)}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="card-body p-0">
                            <StatusPie
                                title={financialTab === "ebitda" ? "EBITDA Breakdown" : "PAT Breakdown"}
                                data={activeProfitData}
                                isCurrency={true}
                                customTotal={activeProfitTotal}
                                hideCardWrapper={true}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* CARD 2: Expense Structure (Fixed & Recurring) */}
            {hasExpenseCards && (
                <div className={hasProfitCards ? "col-xl-5 col-lg-12 mb-3" : "col-12 mb-3"}>
                    <div className="card shadow-sm h-100 border-0 overflow-hidden">
                        <div className="card-header border-0 d-flex justify-content-between align-items-center p-3 bg-transparent">
                            <div className="d-flex align-items-center gap-2">
                                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 38, height: 38, backgroundColor: "rgba(244, 121, 32, 0.1)" }}>
                                    <i className="fas fa-file-invoice-dollar text-warning"></i>
                                </div>
                                <div>
                                    <h6 className="fw-bold mb-0" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>Expense Structure</h6>
                                    <small className="text-muted" style={{ fontSize: 11 }}>Fixed & Recurring Expenses</small>
                                </div>
                            </div>
                            <span className="badge rounded-pill px-3 py-1 fw-bold" style={{ fontSize: 11, background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f8fafc', color: isDark ? '#ffffff' : '#0f172a', border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #e2e8f0' }}>
                                Total: {formatIndianCurrency(totalExpenses)}
                            </span>
                        </div>

                        <div className="card-body p-3 d-flex flex-column justify-content-between" style={{ minHeight: 420 }}>
                            {/* Summary Metric Pills */}
                            <div className="row g-2 mb-3">
                                {canViewFixed && (
                                    <div className="col-6">
                                        <div className="p-3 rounded border text-center" style={{ background: isDark ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc', borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0' }}>
                                            <span className="text-muted small d-block mb-1 fw-semibold">Fixed Expenses</span>
                                            <h5 className="fw-bold mb-0" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>{formatIndianCurrency(totalFixed)}</h5>
                                        </div>
                                    </div>
                                )}
                                {canViewRecurring && (
                                    <div className="col-6">
                                        <div className="p-3 rounded border text-center" style={{ background: isDark ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc', borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0' }}>
                                            <span className="text-muted small d-block mb-1 fw-semibold">Recurring Expenses</span>
                                            <h5 className="fw-bold mb-0" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>{formatIndianCurrency(totalRecurring)}</h5>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Detailed Breakdown or Clean Reconciled State */}
                            {totalExpenses > 0 ? (
                                <StatusPie
                                    title="Expense Distribution"
                                    data={[
                                        { name: "Fixed Expenses", value: totalFixed },
                                        { name: "Recurring Expenses", value: totalRecurring }
                                    ].filter(x => x.value > 0)}
                                    isCurrency={true}
                                    hideCardWrapper={true}
                                />
                            ) : (
                                <div className="text-center py-4 my-auto px-3">
                                    <div className="rounded-circle bg-success-subtle text-success d-inline-flex align-items-center justify-content-center mb-3" style={{ width: 56, height: 56 }}>
                                        <i className="fas fa-shield-alt fs-3"></i>
                                    </div>
                                    <h6 className="fw-bold mb-1" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>Expenses Reconciled</h6>
                                    <p className="text-muted small mb-0">
                                        No unallocated fixed or recurring expenses recorded for the active work orders.
                                    </p>
                                </div>
                            )}

                            <div className="mt-auto pt-2 border-top text-center text-muted" style={{ fontSize: 11 }}>
                                <i className="fas fa-info-circle me-1 text-primary"></i>
                                Expenses are tracked across general overheads and recurring operational cost.
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const ACTION_BUTTONS = [
    { label: "Projects", path: "/projects", icon: "fa-diagram-project", permission: "read:projects" },
    { label: "Work Orders", path: "/work-orders", icon: "fa-clipboard-list", permission: "read:workorders" },
    { label: "Purchase", path: "/purchase-orders", icon: "fa-cart-shopping", permission: "read:pos" },
    { label: "Billings", path: "/billings", icon: "fa-file-invoice", permission: "read:billings" },
    { label: "Commercial Dashboard", path: "/commercial/dashboard", icon: "fa-chart-line", permission: "read:billings" },
    { label: "Inventory", path: "/inventory-dashboard", icon: "fa-warehouse", permission: "read:inventory" },
    { label: "Drawing", path: "/documents?type=Drawing", icon: "fa-pen-ruler", permission: "read:documents" },
    { label: "TDS/Manual", path: "/tds", icon: "fa-file-medical", permission: "read:documents" },
    { label: "TC/Warranty Card", path: "/tc-documents", icon: "fa-check-double", permission: "read:documents" },
    { label: "Statutory Approvals", path: "/statutory-approvals", icon: "fa-file-contract", permission: "read:statutory-approvals" },
    { label: "Pending Approvals", path: "/pending-approvals", icon: "fa-hourglass-half", permission: ["read:pending-approvals", "read:documents", "read:jmrs", "read:mirs", "read:measurement-sheets", "read:moms", "read:tcs", "read:statutory-approvals"] },
    // { label: "Indent", path: "/indent", icon: "fa-indent", permission: "read:indents" },
    // { label: "MIR", path: "/mir", icon: "fa-clipboard-check", permission: "read:mirs" },
    // { label: "JMR", path: "/jmr", icon: "fa-list-check", permission: "read:jmrs" },
    // { label: "DPR", path: "/dpr", icon: "fa-file-alt", permission: "read:dprs" },
    { label: "Manpower Dashboard", path: "/dpr/manpower-dashboard", icon: "fa-hard-hat", permission: "read:dprs" },
    { label: "Hindrance Register", path: "/dpr/hindrance-register", icon: "fa-exclamation-triangle", permission: "read:dprs" },
    { label: "HOTO Dashboard", path: "/handover/readiness-dashboard", icon: "fa-gauge-high", permission: "read:handovers" },
    { label: "Handover", path: "/handover", icon: "fa-handshake", permission: "read:handovers" },
    { label: "Milestone Tracker", path: "/milestones", icon: "fa-flag-checkered", permission: "read:projects" },
    { label: "Risk Register", path: "/risk-register", icon: "fa-triangle-exclamation", permission: "read:projects" },
    { label: "Equipment", path: "/equipment", icon: "fa-cogs", permission: "read:equipment" },
    { label: "QC Inspections", path: "/qc", icon: "fa-microscope", permission: "read:qc" },
    { label: "Safety", path: "/safety", icon: "fa-hard-hat", permission: "read:safety" },
    { label: "Other MOM", path: "/other-mom/list", icon: "fa-file-signature", permission: "read:moms" },
    { label: "Statutory Compliance", path: "/compliance-dossier", icon: "fa-file-contract", permission: "read:compliance-dossiers" },
    { label: "Reimbursements", path: "/staff-reimbursements", icon: "fa-receipt", permission: "read:reimbursements" },
];

const Home = () => {
    const { hasPermission, role: userRole, isAdmin, assignedProjects = [], assignedWOs = [] } = usePermission();
    const [dashboard, setDashboard] = useState(null);
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState("");
    const [user, setUser] = useState(null);
    const [recentNotifications, setRecentNotifications] = useState([]);
    const [refreshing, setRefreshing] = useState(false);
    const [selfAttendance, setSelfAttendance] = useState(null);
    const [qcStats, setQcStats] = useState(null);
    const [safetyStats, setSafetyStats] = useState(null);
    const [checklistStats, setChecklistStats] = useState(null);
    const [userPermissionsReady, setUserPermissionsReady] = useState(false);
    const [selfAttendanceReady, setSelfAttendanceReady] = useState(false);
    const [pendingApprovalsAlertCount, setPendingApprovalsAlertCount] = useState(0);

    const roleName = (user?.role?.name || user?.roleName || userRole || "").toLowerCase();
    const isClientUser = roleName === "client" || roleName === "po";
    const canSeePendingApprovalsButton = isAdmin || isClientUser;
    const hasAssignedScope = isAdmin || assignedProjects.length > 0 || assignedWOs.length > 0;
    const showMyTasksButton = Boolean(user) && !isClientUser;

    const allowedButtons = useMemo(() => {
        return ACTION_BUTTONS.filter(btn => {
            if (btn.label === "Pending Approvals") return false;
            const hasPerm = hasPermission(btn.permission);
            if (!hasPerm) return false;

            // Hide specific buttons for client role as requested
            if (roleName === "client") {
                const clientHidden = ["Projects", "Work Orders", "Purchase", "Billings", "Drawing", "TDS/Manual", "TC/Warranty Card", "Indent"];
                if (clientHidden.includes(btn.label)) return false;
            }

            return true;
        });
    }, [hasPermission, roleName]);

    const hasPendingApprovalsAccess = useMemo(() => {
        return canSeePendingApprovalsButton;
    }, [canSeePendingApprovalsButton]);

    const hasPendingApprovalsAlert = pendingApprovalsAlertCount > 0;
    const showActionButtons = userPermissionsReady && selfAttendanceReady;

    const extractList = useCallback((payload) => {
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.data)) return payload.data;
        if (Array.isArray(payload?.docs)) return payload.docs;
        if (Array.isArray(payload?.items)) return payload.items;
        return [];
    }, []);

    const fetchPendingApprovalsAlertCount = useCallback(async () => {
        if (!canSeePendingApprovalsButton) return;

        const sources = [
            {
                permission: "read:documents",
                endpoint: "/documents",
                params: { status: "Pending Approval", page: 1, limit: 200 },
                count: (items) => items.length,
            },
            {
                permission: "read:dprs",
                requiresAssignedScope: true,
                endpoint: "/dpr",
                params: { page: 1, limit: 200 },
                count: (items) =>
                    items.filter((item) =>
                        ["draft", "submitted"].includes(String(item.status || "").toLowerCase())
                    ).length,
            },
            {
                permission: "read:mirs",
                endpoint: "/mir",
                params: { status: "Pending", page: 1, limit: 200 },
                count: (items) => items.length,
            },
            {
                permission: "read:jmrs",
                endpoint: "/measurement-sheets",
                params: { status: "Submitted", page: 1, limit: 200 },
                count: (items) => items.length,
            },
            {
                permission: "read:moms",
                endpoint: "/mom",
                params: { status: "Pending", page: 1, limit: 200 },
                count: (items) => items.length,
            },
            {
                permission: "read:documents",
                endpoint: "/document-control",
                params: { type: "TDS", status: "Pending Approval", page: 1, limit: 200 },
                count: (items) =>
                    items.filter((item) => String(item.status || "").toLowerCase() !== "approved").length,
            },
            {
                permission: "read:statutory-approvals",
                requiresAssignedScope: true,
                endpoint: "/statutory-approvals",
                params: { status: "Pending", page: 1, limit: 200 },
                count: (items) => items.length,
            },
            {
                permission: "read:billings",
                requiresAssignedScope: true,
                endpoint: "/billing",
                params: { page: 1, limit: 200 },
                count: (items) =>
                    items.filter((item) =>
                        ["draft", "submitted", "pending"].includes(String(item.status || "").toLowerCase())
                    ).length,
            },
        ];

        const visibleSources = sources.filter(
            (source) =>
                hasPermission(source.permission) &&
                (!source.requiresAssignedScope || hasAssignedScope)
        );

        try {
            const results = await Promise.allSettled(
                visibleSources.map(async (source) => {
                    const res = await api.get(source.endpoint, { params: source.params });
                    return source.count(extractList(res.data?.data));
                })
            );

            const count = results.reduce((sum, result) => {
                if (result.status !== "fulfilled") return sum;
                return sum + Number(result.value || 0);
            }, 0);

            setPendingApprovalsAlertCount(count);
        } catch (err) {
            console.error("Pending approvals count error:", err);
            setPendingApprovalsAlertCount(0);
        }
    }, [extractList, hasAssignedScope, hasPermission]);

    const fetchProjects = useCallback(async () => {
        if (!hasPermission("read:workorders")) return;
        try {
            const res = await api.get("/work-orders");
            if (res.data?.success) setProjects(res.data.data || []);
        } catch (err) { console.error("Project fetch error:", err); }
    }, [hasPermission]);

    const fetchUser = useCallback(async () => {
        try {
            const res = await api.get("/auth/me");
            if (res.data.success) setUser(res.data.data);
        } catch (err) { console.error("User fetch error:", err); }
        finally {
            setUserPermissionsReady(true);
        }
    }, []);

    const fetchSelfAttendance = useCallback(async () => {
        try {
            const res = await api.get("/staff-attendance/self/today");
            if (res.data?.success && res.data?.canPunch) {
                setSelfAttendance(res.data);
            }
        } catch (err) {
            setSelfAttendance(null);
        } finally {
            setSelfAttendanceReady(true);
        }
    }, []);

    const fetchQcStats = useCallback(async () => {
        if (!hasPermission("read:qc")) return;
        try {
            const res = await getQCStats();
            if (res.data?.success) setQcStats(res.data.data);
        } catch (_) { }
    }, [hasPermission]);

    const fetchSafetyStats = useCallback(async () => {
        if (!hasPermission("read:safety")) return;
        try {
            const res = await getSafetyStats();
            if (res.data?.success) setSafetyStats(res.data.data);
        } catch (_) { }
    }, [hasPermission]);

    const fetchChecklistStats = useCallback(async () => {
        if (!hasPermission("read:safety")) return;
        try {
            const res = await getChecklistStats();
            if (res.data?.success) setChecklistStats(res.data.data);
        } catch (_) { }
    }, [hasPermission]);

    const fetchDashboardData = useCallback(async () => {
        if (!hasPermission("read:dashboard")) return;
        try {
            const query = selectedProject ? `?project=${selectedProject}` : "";
            const res = await api.get(`/dashboard${query}`);
            if (res.data?.success) setDashboard(res.data.data);
        } catch (err) {
            console.error("Dashboard error:", err);
            if (err.response?.status === 403) setDashboard(null);
        }
    }, [hasPermission, selectedProject]);

    const fetchRecentNotifications = useCallback(async () => {
        const isUserAdmin = userRole === 'admin' || userRole === 'superadmin';
        // Only fetch if user has permission to see activity feed
        if (!isUserAdmin && !hasPermission("view:dashboard:activity") && !hasPermission("read:notifications")) {
            return;
        }

        try {
            const endpoint = isUserAdmin
                ? "/notifications/all?page=1&limit=6"
                : "/notifications?page=1&limit=6";

            const res = await api.get(endpoint);
            if (res?.data?.success) setRecentNotifications(res.data.data || []);
        } catch (err) {
            // Silently fail if 403 to avoid console noise for restricted users
            if (err.response?.status === 403) {
                setRecentNotifications([]);
            } else {
                console.error("Notification fetch error:", err);
            }
        }
    }, [hasPermission, userRole]);

    const handleRefresh = async () => {
        setRefreshing(true);
        await Promise.all([
            fetchDashboardData(),
            fetchRecentNotifications(),
            fetchProjects(),
            fetchQcStats(),
            fetchSafetyStats(),
            fetchChecklistStats(),
            canSeePendingApprovalsButton ? fetchPendingApprovalsAlertCount() : Promise.resolve(),
        ]);
        setRefreshing(false);
    };

    const filteredWorkOrders = useMemo(() => {
        if (!user) return [];
        if (userRole === "admin") return projects;

        // Trust the assignedWorkOrders list populated by the backend auth middleware
        const assignedIds = (user.assignedWorkOrders || []).map(id => id.toString());
        return projects.filter(wo => assignedIds.includes(wo._id.toString()));
    }, [user, userRole, projects]);

    const projectOptions = useMemo(() => {
        const map = new Map();
        (filteredWorkOrders || []).forEach(wo => {
            const p = wo.project;
            if (p && (p._id || p.id)) {
                const id = (p._id || p.id).toString();
                if (!map.has(id)) {
                    map.set(id, {
                        value: id,
                        label: p.projectName || p.name || p.projectCode || "Unnamed Project",
                        project: p
                    });
                }
            }
        });
        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [filteredWorkOrders]);

    const ongoingValueData = useMemo(() => {
        const dataToUse = selectedProject
            ? filteredWorkOrders.filter(wo => {
                const pId = wo.project?._id || (typeof wo.project === "string" ? wo.project : null);
                return pId && pId.toString() === selectedProject;
            })
            : filteredWorkOrders;

        return dataToUse.map((wo) => ({
            name: `${wo.project?.projectName || "Project"} (${wo.woNo || " - "})`,
            value: wo.totalAmount || wo.value || 0,
        }));
    }, [filteredWorkOrders, selectedProject]);

    useEffect(() => { fetchUser(); fetchProjects(); fetchSelfAttendance(); fetchQcStats(); fetchSafetyStats(); fetchChecklistStats(); }, [fetchUser, fetchProjects, fetchSelfAttendance, fetchQcStats, fetchSafetyStats, fetchChecklistStats]);
    useEffect(() => { fetchDashboardData(); fetchRecentNotifications(); }, [fetchDashboardData, fetchRecentNotifications]);
    useEffect(() => {
        if (!canSeePendingApprovalsButton) return;
        fetchPendingApprovalsAlertCount();
    }, [canSeePendingApprovalsButton, fetchPendingApprovalsAlertCount]);

    const taxData = useMemo(() => (dashboard?.company?.taxBreakdown || []).map(p => ({
        name: p.name || "Project",
        value: p.value || 0,
    })), [dashboard]);

    const performaData = useMemo(() => {
        // Performa invoice value should be the same as tax invoice value
        return taxData;
    }, [taxData]);

    const purchaseData = useMemo(() => (dashboard?.company?.purchaseBreakdown || []).map(p => ({
        name: p.name || "Project",
        value: p.value || 0,
    })), [dashboard]);

    const paymentData = useMemo(() => (dashboard?.company?.paymentBreakdownForPie || []).map(p => ({
        name: p.name || "Project",
        value: p.value || 0,
    })), [dashboard]);

    const profitData = useMemo(() => (dashboard?.company?.profitBreakdown || []).map(p => ({
        name: p.name || "Project",
        value: p.value || 0,
    })), [dashboard]);


    const workOrders = dashboard?.workOrders || {};
    const billing = dashboard?.billings || {};

    const getIconByModule = (module) => {
        switch (module) {
            case "DPR": return "fa-file-alt";
            case "Billing": return "fa-file-invoice";
            case "Material": return "fa-box";
            case "WorkOrder": return "fa-clipboard-list";
            default: return "fa-bell";
        }
    };

    const MaterialCard = ({ title, icon, color, data, emptyText }) => {
        const total = useMemo(() => (data || []).reduce((sum, item) => sum + (item.count || 0), 0), [data]);

        return (
            <div className="col-xl-3 col-md-6 mb-4">
                <div className="card shadow-sm h-100 border-0 overflow-hidden">
                    <div className="card-body p-4">
                        <div className="d-flex align-items-center justify-content-between mb-4">
                            <div className="d-flex align-items-center">
                                <div className={`rounded-circle bg-${color}-light d-flex align-items-center justify-content-center me-3 shadow-sm`} style={{ width: 48, height: 48 }}>
                                    <i className={`fas ${icon} text-${color} fs-5`}></i>
                                </div>
                                <h6 className="mb-0 fw-bold">{title}</h6>
                            </div>
                            <span className="badge bg-primary rounded-pill fw-bold" style={{ fontSize: 11 }}>
                                Total: {total}
                            </span>
                        </div>
                        <div className="material-status-list">
                            {data?.length ? data.map((item, idx) => (
                                <div key={idx} className="d-flex justify-content-between align-items-center mb-3 p-2 rounded hover-bg-light transition-all">
                                    <span className="text-muted small fw-semibold d-flex align-items-center">
                                        <span className={`status-dot bg-${color} me-2`} style={{ width: 6, height: 6, borderRadius: '50%' }}></span>
                                        {item._id || "Other"}
                                    </span>
                                    <span className="badge bg-primary fw-bold rounded px-2 py-1" style={{ fontSize: 10 }}>{item.count}</span>
                                </div>
                            )) : (
                                <div className="text-center py-4">
                                    <p className="text-muted small mb-0">{emptyText}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <style>{`
 .dashboard-alert-btn {
 position: relative;
 overflow: visible;
 isolation: isolate;
 z-index: 1;
 }

 .dashboard-alert-dot {
 position: absolute;
 top: -6px;
 right: -6px;
 width: 12px;
 height: 12px;
 border-radius: 9999px;
 background: #dc3545;
 box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.55);
 animation: dashboard-alert-pulse 1.6s ease-out infinite;
 pointer-events: none;
 z-index: 2;
 }

 .dashboard-alert-dot::after {
 content: "";
 position: absolute;
 inset: -8px;
 border-radius: inherit;
 border: 1px solid rgba(220, 53, 69, 0.28);
 }

 @keyframes dashboard-alert-pulse {
 0% {
 transform: scale(0.9);
 box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.45);
 }
 70% {
 transform: scale(1);
 box-shadow: 0 0 0 10px rgba(220, 53, 69, 0);
 }
 100% {
 transform: scale(0.9);
 box-shadow: 0 0 0 0 rgba(220, 53, 69, 0);
 }
 }
 `}</style>
            <div className="container-fluid p-0">
                <div className="row">
                    <div className="col-12 mb-4">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-4 mb-4 p-2">
                            <div className="d-flex align-items-center gap-3">
                                <div>
                                    <h3 className="fw-bold mb-1">Dashboard Overview</h3>
                                    <p className="text-muted mb-0 small">Real-time monitoring of projects, billing, materials and execution progress</p>
                                </div>
                                <Button
                                    variant="light"
                                    className="btn-icon-only rounded-circle shadow-sm d-flex align-items-center justify-content-center p-0"
                                    style={{ width: 40, height: 40, border: '1px solid var(--border-color, #eee)' }}
                                    onClick={handleRefresh}
                                    disabled={refreshing}
                                    title="Refresh Dashboard"
                                >
                                    {refreshing ? (
                                        <Spinner animation="border" size="sm" />
                                    ) : (
                                        <i className="fas fa-sync-alt text-primary"></i>
                                    )}
                                </Button>
                            </div>

                            {showActionButtons ? (
                                <div className="d-flex align-items-center gap-3 flex-wrap">
                                    <div style={{ flex: "0 0 210px", minWidth: 210 }}>
                                        {hasPendingApprovalsAccess ? (
                                            <Link
                                                to="/pending-approvals"
                                                className="btn btn-sm w-100 d-flex align-items-center justify-content-center gap-2 rounded-pill px-4 py-2 fw-semibold transition-all shadow-sm-hover position-relative dashboard-alert-btn"
                                                style={{
                                                    minHeight: 48,
                                                    backgroundColor: "#d97706",
                                                    borderColor: "#d97706",
                                                    color: "#fff",
                                                }}
                                            >
                                                <i className="fas fa-hourglass-half text-white"></i>
                                                Pending Approvals
                                                {hasPendingApprovalsAlert && <span className="dashboard-alert-dot" />}
                                            </Link>
                                        ) : (
                                            <div
                                                className="btn btn-sm w-100 d-flex align-items-center justify-content-center gap-2 rounded-pill px-4 py-2 fw-semibold position-relative dashboard-alert-btn"
                                                style={{
                                                    minHeight: 48,
                                                    backgroundColor: "#d97706",
                                                    borderColor: "#d97706",
                                                    color: "#fff",
                                                    visibility: "hidden",
                                                    pointerEvents: "none",
                                                }}
                                                aria-hidden="true"
                                            >
                                                <i className="fas fa-hourglass-half text-white"></i>
                                                Pending Approvals
                                            </div>
                                        )}
                                    </div>
                                    {user && (
                                        <div style={{ width: "100%", minWidth: 350, flex: "1 1 350px" }}>
                                            <Select
                                                value={selectedProject ? projectOptions.find(o => o.value === selectedProject) : null}
                                                onChange={(opt) => setSelectedProject(opt ? opt.value : "")}
                                                options={projectOptions}
                                                isClearable
                                                placeholder="Filter by Project"
                                                styles={{
                                                    control: (b) => ({ ...b, borderRadius: 12, minHeight: 48, border: '1px solid #eee', boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }),
                                                    placeholder: (b) => ({ ...b, color: '#999', fontSize: '14px' })
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="d-flex align-items-center gap-3 flex-wrap" style={{ minHeight: 48 }} aria-hidden="true" />
                            )}
                        </div>

                        {showActionButtons && (allowedButtons.length > 0 || selfAttendance?.canPunch || showMyTasksButton) && (
                            <div className="d-flex flex-wrap gap-2 mb-4 px-2">
                                {showMyTasksButton && (
                                    <Link
                                        to="/my-tasks"
                                        className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold transition-all shadow-sm-hover"
                                    >
                                        <i className="fas fa-tasks"></i> My Tasks
                                    </Link>
                                )}
                                {selfAttendance?.canPunch && (
                                    <Link
                                        to="/my-attendance"
                                        className="btn btn-outline-success btn-sm d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold transition-all shadow-sm-hover"
                                    >
                                        <i className="fas fa-clock"></i> My Attendance
                                    </Link>
                                )}
                                {allowedButtons.map((btn) => (
                                    <Link
                                        key={btn.path}
                                        to={btn.path}
                                        className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold transition-all shadow-sm-hover"
                                    >
                                        <i className={`fas ${btn.icon}`}></i> {btn.label}
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>

                    <PermissionWrapper user={user} required={["view:dashboard:stats"]}>
                        <div className="row g-3 mb-4">
                            {[
                                { title: "Assigned Work Orders", value: workOrders.count || 0, icon: "fa-layer-group", color: "#0078D7", bg: "rgba(0, 120, 215, 0.08)" },
                                { title: "Total Value", value: `\u20B9${(workOrders.totalValue || 0).toLocaleString()}`, icon: "fa-rupee-sign", color: "#28A745", bg: "rgba(40, 167, 69, 0.08)" },
                                { title: "Overdue Work Orders", value: workOrders.overdue || 0, icon: "fa-exclamation-circle", color: "#DC3545", bg: "rgba(220, 53, 69, 0.08)" },
                                { title: "Active Projects", value: dashboard?.company?.ongoingProjects || 0, icon: "fa-project-diagram", color: "#F47920", bg: "rgba(244, 121, 32, 0.08)" },
                            ].map((item, i) => (
                                <div key={i} className="col-xl-3 col-sm-6">
                                    <div className="card shadow-sm border-0 h-100 stat-card" style={{ borderLeft: `4px solid ${item.color}`, transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}>
                                        <div className="card-body d-flex align-items-center">
                                            <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: 50, height: 50, backgroundColor: item.bg }}>
                                                <i className={`fas ${item.icon}`} style={{ color: item.color, fontSize: '18px' }}></i>
                                            </div>
                                            <div>
                                                <p className="text-muted mb-0 small fw-bold">{item.title}</p>
                                                <h4 className="fw-bold mb-0" style={{ fontSize: '20px' }}>{item.value}</h4>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </PermissionWrapper>

                    <PermissionWrapper user={user} required={["view:dashboard:charts"]}>
                        <div className="row g-3 mb-4">
                            {[
                                { id: "wo", title: "Work Order Value", data: ongoingValueData, isCurrency: true, show: true },
                                { id: "performa", title: "Performa Invoice Value", data: performaData, isCurrency: true, show: true },
                                { id: "tax", title: "Tax Invoice Value", data: taxData, isCurrency: true, show: true },
                                { id: "po", title: "Purchase Orders", data: purchaseData, isCurrency: true, show: !isClientUser },
                                { id: "payment", title: isClientUser ? "Payment Released" : "Payment Received", data: paymentData, isCurrency: true, show: true },
                                { id: "pnl", title: "Profit & Loss Statement", data: profitData, isCurrency: true, show: !isClientUser },
                            ].filter(item => item.show).map((cfg) => {
                                const activeCount = (cfg.data || []).filter(d => Number(d.value || 0) > 0).length;
                                const isWide = activeCount > 10;
                                const colClass = isWide ? "col-12 mb-4" : "col-xl-4 col-lg-6 col-md-6 mb-4";

                                return (
                                    <div key={cfg.id} className={colClass}>
                                        <StatusPie
                                            title={cfg.title}
                                            data={cfg.data}
                                            isCurrency={cfg.isCurrency}
                                            isWide={isWide}
                                        />
                                    </div>
                                );
                            })}
                        </div>

                        {!isClientUser && (
                            <FinancialGraph
                                data={dashboard?.company?.financialBreakdown}
                                totals={{
                                    ebitda: dashboard?.company?.ebitda,
                                    pat: dashboard?.company?.pat
                                }}
                                hasPermission={hasPermission}
                            />
                        )}
                    </PermissionWrapper>

                    <div className="row g-3 mb-4">
                        <PermissionWrapper user={user} required={["view:dashboard:progress"]}>
                            <div className="col-xl-8 col-lg-7">
                                <div className="card shadow-sm border-0 h-100 d-flex flex-column">
                                    <div className="card-header bg-transparent border-0 p-3 d-flex justify-content-between align-items-center">
                                        <div>
                                            <h5 className="mb-0 fw-bold">Work Order Progress</h5>
                                            <small className="text-muted" style={{ fontSize: 11 }}>Status distribution of assigned work orders</small>
                                        </div>
                                    </div>
                                    <div className="card-body p-2 d-flex flex-column justify-content-center" style={{ height: 380, minHeight: 380 }}>
                                        <ChartBarApex data={workOrders.woSummary || []} height={350} />
                                    </div>
                                </div>
                            </div>
                        </PermissionWrapper>

                        <PermissionWrapper user={user} required={["view:dashboard:activity"]}>
                            <div className="col-xl-4 col-lg-5">
                                <div className="card shadow-sm border-0 h-100 d-flex flex-column">
                                    <div className="card-header bg-transparent border-0 p-3 d-flex justify-content-between align-items-center">
                                        <div>
                                            <h5 className="mb-0 fw-bold">Recent Activity</h5>
                                            <small className="text-muted" style={{ fontSize: 11 }}>Audit trail & updates</small>
                                        </div>
                                        <span className="badge bg-light text-muted border rounded-pill px-2 py-1" style={{ fontSize: 11 }}>
                                            {recentNotifications.length} updates
                                        </span>
                                    </div>
                                    <div className="card-body p-0 d-flex flex-column" style={{ height: 380, maxHeight: 380, overflowY: "auto" }}>
                                        {recentNotifications.length > 0 ? (
                                            <div className="list-group list-group-flush">
                                                {recentNotifications.map((n) => (
                                                    <div key={n._id} className="list-group-item bg-transparent border-bottom border-light px-3 py-2">
                                                        <div className="d-flex align-items-start gap-3">
                                                            <div className={`rounded-circle bg-${n.action === 'create' ? 'success' : 'warning'} text-white d-flex align-items-center justify-content-center flex-shrink-0 shadow-sm`} style={{ width: 32, height: 32 }}>
                                                                <i className={`fas ${getIconByModule(n.module)} fs-12`}></i>
                                                            </div>
                                                            <div className="flex-grow-1 min-w-0">
                                                                <p className="mb-0 small" style={{ lineHeight: 1.4 }}>
                                                                    <strong>{n.user?.name || "System"}</strong> {n.message}
                                                                </p>
                                                                <small className="text-muted" style={{ fontSize: 10 }}>
                                                                    {new Date(n.createdAt).toLocaleDateString()} • {new Date(n.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted p-4">
                                                <i className="fas fa-bell-slash fs-3 mb-2 text-light"></i>
                                                <p className="small mb-0">No recent activity</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </PermissionWrapper>
                    </div>

                    {!isClientUser && (
                        <div className="row g-3 mb-4">
                            <PermissionWrapper user={user} required={["view:dashboard:map"]}>
                                <div className="col-xl-6">
                                    <IndiaMap data={projects} />
                                </div>
                            </PermissionWrapper>
                            <PermissionWrapper user={user} required={["view:dashboard:charts"]}>
                                <div className="col-xl-6">
                                    <div className="card shadow-sm border-0 h-100 d-flex flex-column">
                                        <div className="card-header bg-transparent border-0 p-3 d-flex justify-content-between align-items-center">
                                            <div>
                                                <h5 className="mb-0 fw-bold">{isClientUser ? "Billing vs Payments Released" : "Billing vs Payments"}</h5>
                                                <small className="text-muted" style={{ fontSize: 11 }}>Monthly financial trend</small>
                                            </div>
                                        </div>
                                        <div className="card-body p-2 d-flex flex-column justify-content-center" style={{ height: 380, minHeight: 380 }}>
                                            <ReservationChart data={dashboard?.billingList || []} height={350} />
                                        </div>
                                    </div>
                                </div>
                            </PermissionWrapper>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default Home;




